import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ArInvoiceListItemSchema,
  InvoiceStatusSchema,
} from "./ar-invoices.contract";

/**
 * AR Custom Aging Report RPC Contract
 *
 * Provides an endpoint for fetching aging data with dynamic day ranges,
 * optional customer filters, status filters, and a dual calculation basis
 * (days overdue vs days since invoice date).
 *
 * Returns both customer-grouped totals and a flat invoice list in one call.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/**
 * Shared view mode for aging reports (used by custom aging, saved reports, and PDF export).
 */
export const ArAgingViewModeSchema = z.enum([
  "grouped",
  "flat",
  "grouped_detail",
]);

const UUIDSchema = z.uuid();

/**
 * Input for the custom aging report
 */
export const GetArCustomAgingInputSchema = z
  .object({
    /**
     * Minimum days aged (inclusive). 0 means "no minimum" — all invoices
     * regardless of age pass this filter. To filter to invoices that are not
     * yet due (the old "current" bucket), set `currentOnly: true` instead.
     */
    minDays: z.int().min(0),
    /**
     * Maximum days (inclusive). Omit for open-ended (e.g., 90+ means minDays=91, no maxDays).
     */
    maxDays: z.int().min(0).optional(),
    /**
     * When true, restrict results to invoices whose reference date is in the
     * future ("not yet due" / "current bucket"). Only meaningful when
     * basis === "days_overdue"; for ship/invoice-date bases the reference
     * date is rarely in the future. When set, minDays/maxDays are ignored.
     */
    currentOnly: z.boolean().optional(),
    /**
     * Calculation basis:
     *   "days_overdue"         = days past due date (dueDate < today)
     *   "days_since_invoice"   = days since invoice date (invoiceDate < today)
     *   "days_since_shipment"  = days since ship date (falls back to invoiceDate)
     */
    basis: z
      .enum(["days_overdue", "days_since_invoice", "days_since_shipment"])
      .default("days_since_shipment"),
    /**
     * Filter to specific customers by ID.
     */
    customerIds: z.array(UUIDSchema).optional(),
    /**
     * Filter by invoice status. Defaults server-side to excluding closed/voided
     * if not provided (matching existing aging behavior).
     */
    status: z.array(InvoiceStatusSchema).optional(),
    /**
     * Sort order for the grouped customer view.
     */
    sortBy: z
      .enum([
        "customerName",
        "openBalance",
        "invoiceCount",
        "lastStatementSentAt",
      ])
      .optional(),
    sortDirection: z.enum(["asc", "desc"]).optional(),
    /**
     * Filter by how long it has been since a customer was last sent a statement
     * email (topic = "accounts_receivable.statement", status = "sent").
     *
     * - lastStatementSentMinDays: include customers whose last send was at
     *   least N days ago (last_sent_at <= today - N days).
     * - lastStatementSentMaxDays: include customers whose last send was at
     *   most N days ago (last_sent_at >= today - N days).
     * - includeNeverSentStatement: when true, also include customers with no
     *   prior statement send. When this is true and no min/max is provided,
     *   only never-sent customers are returned.
     */
    lastStatementSentMinDays: z.int().min(0).optional(),
    lastStatementSentMaxDays: z.int().min(0).optional(),
    includeNeverSentStatement: z.boolean().optional(),
  })
  .refine(
    (data) => data.maxDays === undefined || data.maxDays >= data.minDays,
    {
      message: "maxDays must be >= minDays",
      path: ["maxDays"],
    },
  )
  .refine(
    (data) =>
      data.lastStatementSentMaxDays === undefined ||
      data.lastStatementSentMinDays === undefined ||
      data.lastStatementSentMaxDays >= data.lastStatementSentMinDays,
    {
      message: "lastStatementSentMaxDays must be >= lastStatementSentMinDays",
      path: ["lastStatementSentMaxDays"],
    },
  );

/**
 * A single customer's custom aging row
 */
export const ArCustomAgingCustomerRowSchema = z.object({
  customerId: UUIDSchema,
  customerName: z.string().nullable(),
  /** Sum of balance_due across this customer's invoices in the requested day range */
  openBalance: DecimalStringSchema,
  /** Count of invoices for this customer in the requested day range */
  invoiceCount: z.int().nonnegative(),
  /**
   * ISO timestamp of the most recent AR statement email sent to this customer
   * (topic = "accounts_receivable.statement", status = "sent"). Null when the
   * customer has never been sent a statement.
   */
  lastStatementSentAt: z.iso.datetime().nullable(),
});

/**
 * Grand totals row across all customers
 */
export const ArCustomAgingTotalsSchema = z.object({
  openBalance: DecimalStringSchema,
  invoiceCount: z.int().nonnegative(),
});

/**
 * Get AR custom aging response schema
 */
export const GetArCustomAgingResponseSchema = z.object({
  /** Customer-grouped totals */
  customers: z.array(ArCustomAgingCustomerRowSchema),
  /** Grand totals across all customers */
  totals: ArCustomAgingTotalsSchema,
  /** Flat invoice list for the matching invoices (capped at 500 rows) */
  invoices: z.array(ArInvoiceListItemSchema),
});

/**
 * AR Custom Aging Contracts
 */
/**
 * Response schema for the PDF download endpoint.
 * Returns a base64-encoded PDF since oRPC is JSON-based.
 */
export const DownloadArCustomAgingPdfResponseSchema = z.object({
  /** base64-encoded PDF bytes */
  pdf: z.string(),
  /** Suggested filename for the download */
  filename: z.string(),
});

/**
 * Columns the on-screen flat invoice table can sort by. Used to mirror the
 * user's chosen sort in the PDF export.
 */
export const ArInvoiceFlatSortBySchema = z.enum([
  "invoiceNumber",
  "customerName",
  "invoiceDate",
  "dueDate",
  "shipDate",
  "netAmount",
  "appliedAmount",
  "balanceDue",
  "status",
  "firstSentAt",
  "daysOut",
]);

export const DownloadArCustomAgingPdfInputSchema =
  GetArCustomAgingInputSchema.safeExtend({
    viewMode: ArAgingViewModeSchema,
    /** User's flat-view sort column (only honored when viewMode is "flat"). */
    flatSortBy: ArInvoiceFlatSortBySchema.optional(),
    /** User's flat-view sort direction (only honored when viewMode is "flat"). */
    flatSortDir: z.enum(["asc", "desc"]).optional(),
  });

export const arCustomAgingContracts = {
  get: oc
    .input(GetArCustomAgingInputSchema)
    .output(GetArCustomAgingResponseSchema)
    .route({ method: "GET" }),
  downloadPdf: oc
    .input(DownloadArCustomAgingPdfInputSchema)
    .output(DownloadArCustomAgingPdfResponseSchema)
    .route({ method: "POST" }),
} as const;

// Type exports
export type DownloadArCustomAgingPdfInput = z.infer<
  typeof DownloadArCustomAgingPdfInputSchema
>;
export type GetArCustomAgingInput = z.infer<typeof GetArCustomAgingInputSchema>;
export type ArCustomAgingCustomerRow = z.infer<
  typeof ArCustomAgingCustomerRowSchema
>;
export type ArCustomAgingTotals = z.infer<typeof ArCustomAgingTotalsSchema>;
export type GetArCustomAgingResponse = z.infer<
  typeof GetArCustomAgingResponseSchema
>;
export type DownloadArCustomAgingPdfResponse = z.infer<
  typeof DownloadArCustomAgingPdfResponseSchema
>;
export type ArAgingViewMode = z.infer<typeof ArAgingViewModeSchema>;
export type ArInvoiceFlatSortBy = z.infer<typeof ArInvoiceFlatSortBySchema>;
